import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X } from "lucide-react";

/**
 * Profile-completeness banner for prescribers.
 *
 * The eRx / LifeFile order builder stamps the prescriber's NPI, license
 * state(s) and practice address onto every prescription. If any are missing,
 * LifeFile rejects the order. This banner surfaces exactly what's missing on
 * the prescriber's own dashboard so they can fix it BEFORE an order is blocked.
 *
 * NOTE: DEA is intentionally NOT required — GLPFlo only prescribes
 * non-controlled compounded GLP-1s, so a DEA number is never needed to save
 * a profile or send an order.
 *
 * Dismissal is keyed on the specific set of missing fields, so dismissing
 * won't nag on every reload but the banner returns if a new field goes empty.
 */
const REQUIRED = [
  { keys: ["npi_number"], label: "NPI number" },
  { keys: ["license_states"], label: "License state(s)", isList: true },
  { keys: ["address_street"], label: "Practice street address" },
  { keys: ["address_city"], label: "Practice city" },
  { keys: ["address_state"], label: "Practice state" },
  { keys: ["address_zip"], label: "Practice ZIP" },
];

const ADDRESS_KEYS = ["address_street", "address_city", "address_state", "address_zip"];

function computeMissing(profile) {
  if (!profile) return [];
  return REQUIRED.filter((f) => {
    const raw = profile[f.keys[0]];
    if (f.isList) {
      if (Array.isArray(raw)) return raw.length === 0;
      return !(raw && String(raw).trim());
    }
    return !(raw && String(raw).trim());
  }).map((f) => f.label);
}

function isAddressIncomplete(profile) {
  if (!profile) return false;
  return ADDRESS_KEYS.some((k) => !(profile[k] && String(profile[k]).trim()));
}

export default function PrescriberProfileWarning({ onGoToProfile }) {
  const [missing, setMissing] = useState([]);
  const [addrIncomplete, setAddrIncomplete] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    API.get("/staff/profile")
      .then(({ data }) => {
        if (!active) return;
        const m = computeMissing(data);
        setMissing(m);
        setAddrIncomplete(isAddressIncomplete(data));
        const key = `glpflo_profile_warn_dismissed_${m.slice().sort().join("|")}`;
        setDismissed(m.length > 0 && localStorage.getItem(key) === "1");
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (missing.length === 0 || dismissed) return null;

  const dismiss = () => {
    const key = `glpflo_profile_warn_dismissed_${missing.slice().sort().join("|")}`;
    localStorage.setItem(key, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="prescriber-profile-warning"
      className="mb-6 rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            Your prescriber profile is incomplete
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-1">
            These fields are stamped onto every eRx and are required before an
            order can be sent to the pharmacy. Missing:{" "}
            <span className="font-medium" data-testid="prescriber-profile-missing-list">
              {missing.join(", ")}
            </span>
            .
          </p>
          {addrIncomplete && (
            <div
              className="mt-3 rounded-lg border border-rose-300 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/30 px-3 py-2"
              data-testid="prescriber-profile-address-callout"
            >
              <p className="text-xs font-semibold text-rose-800 dark:text-rose-200">
                Practice mailing address required for pharmacy routing
              </p>
              <p className="text-[11px] text-rose-700 dark:text-rose-300/90 mt-0.5 leading-relaxed">
                Your full practice street, city, state and ZIP are stamped on the
                pharmacy order. Until they&rsquo;re filled in, approved orders are
                <b> silently blocked from being sent to the pharmacy</b> — nothing ships.
              </p>
            </div>
          )}
          <Button
            size="sm"
            className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
            onClick={onGoToProfile}
            data-testid="prescriber-profile-warning-cta"
          >
            Complete my profile
          </Button>
        </div>
        <button
          onClick={dismiss}
          className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 shrink-0"
          title="Dismiss until a field changes"
          data-testid="prescriber-profile-warning-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
